/* eslint-disable max-lines */
/* eslint-disable complexity */
import { nullToUndefined } from '@strata/helper-functions';
import { WarningDialog } from '@strata/react-dialog';
import { FormContainer } from '@strata/react-form-container';
import { InlineMessage } from '@strata/react-inline-message';
import {
    NumberInputFormik,
    PhoneInputFormik,
    TextAreaFormik,
    TextInputFormik,
} from '@strata/react-inputs';
import { DetailLabel } from '@strata/react-label-value-pairs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { RichTextAreaFormik } from '@strata/react-rich-text-area';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { Tag } from '@strata/react-tags';
import { SuccessToast } from '@strata/react-toast-message';
import { dialogStrings, validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';

import { ListPageHeader } from 'components/ListPageHeader';
import { CREATE_ROUTE, PayerOption } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { stripHTMLTags } from 'services/Helpers';
import { post, put, useGet } from 'services/Http';
import { usePermissionCheck } from 'services/Permissions';

/** Default # of days before intake case is automatically submitted to payer. Can be overridden by segment */
const INTAKE_CASE_SLA_DEFAULT_THRESHOLD_DAYS = 3;
/** Default # of days before a submitted intake case is automatically archived. Can be overridden by segment */
const INTAKE_CASE_ARCHIVE_DEFAULT_THRESHOLD_DAYS = 14;

const PayerMarketOptions = {
    CREATE_NEW: 'Create New',
    ADD_EXISTING: 'Add To Existing',
};

export const PayerSegmentDetails = () => {
    const writeIntakeCaseAdmin = usePermissionCheck('write:intake_case_admin');
    const writePayerRestrictedAccess = usePermissionCheck('write:payer_restricted_access');
    const { segmentId } = useParams();
    const navigate = useNavigate();
    const [showWarning, setShowWarning] = useState(false);

    const { lucentEnhancedEligibility, enableNonPaClaimsBasedIntake } = useFlags();
    const goHome = () => navigate('/payer-segment');

    const isCreate = segmentId === CREATE_ROUTE;

    const payerSegment = useGet(!isCreate ? `/reference/payer_segments/${segmentId}` : null);

    const pbmConfig = useGet(!isCreate ? `/pbm/lock_configs/${segmentId}/display` : null);
    const intakeCaseSegmentConfig = useGet(
        !isCreate ? `/intake-case/payer_segment_config/${segmentId}` : null,
    );
    const restrictedGroups = useGet(!isCreate ? `/aperture/restricted_groups/${segmentId}` : null);

    const prescFaxOption = useGet(
        payerSegment
            ? `/clients/admin/${payerSegment.payer_id}/${segmentId}/payer_option/${PayerOption.PRESC_FAX}`
            : undefined,
    );
    const payerSegmentBillingInstructions = useGet(
        !isCreate ? `/reference/payer_segments/${segmentId}/billing_instructions` : null,
    );

    const payers = useGet('/reference/payers/');
    const payersList = (payers ?? []).map((payer) => ({
        name: payer.payer_name,
        secondaryData: payer.payer_id,
    }));

    const payerSegments = useGet('/reference/payer_segments');
    const segmentNamesSet = new Set(
        (payerSegments ?? [])
            .map((segment) => segment.segment_name)
            .filter((segmentName) => segmentName !== payerSegment?.segment_name),
    );

    const payerSegmentOptions = useGet(
        payerSegment
            ? `/treatment/admin/${payerSegment.payer_id}/payer_segment_options/${segmentId}`
            : null,
    );

    const payerMarkets = useGet('/reference/payer_markets');
    const payerMarketOptions = (payerMarkets ?? []).map((payerMarket) => ({
        name: payerMarket.payer_market_name,
        payerMarketId: payerMarket.payer_market_id,
    }));

    const delayUnmanagementConfig = useGet(
        payerSegment
            ? `/scope-assessor/${payerSegment.payer_id}/benefit_check_config/${segmentId}`
            : null,
    );
    const testClaimRejectCodeOverride = useGet(
        payerSegment
            ? `/treatment/admin/${payerSegment.payer_id}/available-integrations/is-test-claim-enabled/${segmentId}`
            : null,
    );
    const testClaimRejectCodeOverrideEnabled = testClaimRejectCodeOverride != null;

    return (
        <Formik
            initialValues={{
                payerId: payerSegment?.payer_id,
                segmentName: payerSegment?.segment_name,
                pbmName: pbmConfig?.pbm_name ?? undefined,
                testClaimRejectCodeOverride: testClaimRejectCodeOverride ? 'Yes' : 'No',
                sendPriorAuthRequest: intakeCaseSegmentConfig?.send_prior_auth_request
                    ? 'Yes'
                    : 'No',
                intakeCaseFaxNumber: nullToUndefined(intakeCaseSegmentConfig?.fax_number),
                intakeCaseSlaOverride:
                    intakeCaseSegmentConfig?.intake_case_sla_override ??
                    INTAKE_CASE_SLA_DEFAULT_THRESHOLD_DAYS,
                intakeCaseArchiveOverride:
                    intakeCaseSegmentConfig?.intake_case_archive_override ??
                    INTAKE_CASE_ARCHIVE_DEFAULT_THRESHOLD_DAYS,
                restrictedGroupIds: (restrictedGroups ?? []).map((group) => group.benefit_group),
                sendPrescriberFax: prescFaxOption?.option_value === 'ON' ? 'Yes' : 'No',
                useCBI: payerSegmentOptions?.USE_CBI === 'ON' ? 'Yes' : 'No',
                useNonPaCbi: payerSegmentOptions?.NONPA_CBI === 'ON' ? 'Yes' : 'No',
                graceFills: payerSegmentOptions?.GRACEFILLS ?? '1',
                useReferralCreationPolicy: payerSegmentOptions?.RF_CRT_POL === 'ON' ? 'Yes' : 'No',
                pharmacyBillingInstructions: nullToUndefined(
                    payerSegmentBillingInstructions?.pharmacy_billing_instructions,
                ),
                medicalBillingInstructions: nullToUndefined(
                    payerSegmentBillingInstructions?.medical_billing_instructions,
                ),
                dispensingFeeAmount: nullToUndefined(payerSegment?.dispensing_fee_amount),
                claimsRebillingThreshold: nullToUndefined(payerSegment?.claims_rebilling_threshold),
                contractEntity: nullToUndefined(payerSegment?.contract_entity),
                bin: nullToUndefined(payerSegmentBillingInstructions?.bin),
                pcn: nullToUndefined(payerSegmentBillingInstructions?.pcn),
                rxGroup: nullToUndefined(payerSegmentBillingInstructions?.rx_group),
                payerMarket: !isCreate ? PayerMarketOptions.ADD_EXISTING : undefined,
                payerMarketId: payerSegment?.payer_market_id,
                copayMessageTitle: nullToUndefined(payerSegmentOptions?.CPY_MSG_TT),
                copayMessageShortDescription: nullToUndefined(payerSegmentOptions?.CPY_MSG_S),
                copayMessageLongDescription: nullToUndefined(payerSegmentOptions?.CPY_MSG_L),
                useDelayedUnmanagement: delayUnmanagementConfig?.use_delayed_unmanagement
                    ? 'Yes'
                    : 'No',
                delayedUnmanagementDays: nullToUndefined(
                    delayUnmanagementConfig?.delayed_unmanagement_days,
                ),
            }}
            enableReinitialize
            validationSchema={Yup.object({
                payerId: Yup.string().required(validationStrings.requiredField.errorMessage),
                segmentName: Yup.string()
                    .max(50, validationStrings.maxCharacter[50])
                    .required(validationStrings.requiredField.errorMessage)
                    .test(
                        'is-name-taken',
                        'A payer segment with this name already exists',
                        (value) => !segmentNamesSet.has(value),
                    ),
                pbmName: Yup.string(),
                sendPrescriberFax: Yup.string()
                    .oneOf(['Yes', 'No'])
                    .required(validationStrings.requiredField.errorMessage),
                useCBI: Yup.string()
                    .oneOf(['Yes', 'No'])
                    .required(validationStrings.requiredField.errorMessage),
                useNonPaCbi: Yup.string()
                    .oneOf(['Yes', 'No'])
                    .required(validationStrings.requiredField.errorMessage),
                graceFills: Yup.string().when('useNonPaCbi', {
                    is: 'Yes',
                    then: (schema) =>
                        schema
                            .oneOf(['1', '2', '3', '4'])
                            .required(validationStrings.requiredField.errorMessage),
                    otherwise: (schema) => schema.notRequired(),
                }),
                useReferralCreationPolicy: Yup.string(),
                testClaimRejectCodeOverride: testClaimRejectCodeOverrideEnabled
                    ? Yup.string()
                          .oneOf(['Yes', 'No'])
                          .required(validationStrings.requiredField.errorMessage)
                    : Yup.string().notRequired(),
                sendPriorAuthRequest: Yup.string()
                    .oneOf(['Yes', 'No'])
                    .required(validationStrings.requiredField.errorMessage),
                intakeCaseFaxNumber: PhoneInputFormik.validator(),
                intakeCaseSlaOverride: Yup.number().required(),
                intakeCaseArchiveOverride: Yup.number().required(),
                restrictedGroupIdsToAdd: Yup.string(),
                restrictedGroupIds: Yup.array(),
                pharmacyBillingInstructions: Yup.string(),
                medicalBillingInstructions: Yup.string(),
                dispensingFeeAmount: Yup.number(),
                claimsRebillingThreshold: Yup.number(),
                contractEntity: Yup.string(),
                bin: Yup.string(),
                pcn: Yup.string(),
                rxGroup: Yup.string(),
                payerMarket: Yup.string().required(validationStrings.requiredField.errorMessage),
                payerMarketName: Yup.string().when('payerMarket', {
                    is: PayerMarketOptions.CREATE_NEW,
                    then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
                }),
                payerMarketId: Yup.string().when('payerMarket', {
                    is: PayerMarketOptions.ADD_EXISTING,
                    then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
                }),
                copayMessageTitle: Yup.string(),
                copayMessageShortDescription: Yup.string()
                    .transform((value) => stripHTMLTags(value))
                    .max(250, validationStrings.maxCharacter.lambda(250)),
                copayMessageLongDescription: Yup.string()
                    .transform((value) => stripHTMLTags(value))
                    .max(1500, validationStrings.maxCharacter.lambda(1500)),
                useDelayedUnmanagement: Yup.string()
                    .oneOf(['Yes', 'No'])
                    .required(validationStrings.requiredField.errorMessage),
                delayedUnmanagementDays: Yup.number()
                    .nullable()
                    .when('useDelayedUnmanagement', {
                        is: 'Yes',
                        then: (schema) =>
                            schema
                                .required(validationStrings.requiredField.errorMessage)
                                .positive(validationStrings.number.positiveNumbersOnly),
                        otherwise: (schema) => schema.notRequired(),
                    }),
            })}
            onSubmit={async (values) => {
                try {
                    let newSegmentId = segmentId;
                    if (isCreate) {
                        const newSegment = await post('/reference/payer_segments/', {
                            segment_name: values.segmentName,
                            payer_id: values.payerId,
                            dispensing_fee_amount: values.dispensingFeeAmount,
                            claims_rebilling_threshold: values.claimsRebillingThreshold,
                            payer_market_id: values.payerMarketId,
                            payer_market_name: values.payerMarketName,
                        });
                        newSegmentId = newSegment.segment_id;
                    }

                    const optionInputs = {};

                    optionInputs.USE_CBI = values.useCBI === 'Yes' ? 'ON' : 'OFF';
                    optionInputs.NONPA_CBI = values.useNonPaCbi === 'Yes' ? 'ON' : 'OFF';
                    optionInputs.GRACEFILLS =
                        values.useNonPaCbi === 'Yes' ? values.graceFills : '1';
                    optionInputs.RF_CRT_POL =
                        values.useReferralCreationPolicy === 'Yes' ? 'ON' : 'OFF';
                    optionInputs.CPY_MSG_TT = values.copayMessageTitle
                        ? values.copayMessageTitle
                        : null;
                    optionInputs.CPY_MSG_S = values.copayMessageShortDescription
                        ? values.copayMessageShortDescription
                        : null;
                    optionInputs.CPY_MSG_L = values.copayMessageLongDescription
                        ? values.copayMessageLongDescription
                        : null;

                    await Promise.all([
                        ...(!isCreate
                            ? [
                                  put(`/reference/payer_segments/${newSegmentId}`, undefined, {
                                      segment_name: values.segmentName,
                                      ...(values.dispensingFeeAmount
                                          ? { dispensing_fee_amount: values.dispensingFeeAmount }
                                          : {}),
                                      ...(values.claimsRebillingThreshold
                                          ? {
                                                claims_rebilling_threshold:
                                                    values.claimsRebillingThreshold,
                                            }
                                          : {}),
                                      contract_entity: values.contractEntity,
                                  }),
                              ]
                            : []),
                        put(`/clients/admin/${values.payerId}/${newSegmentId}/payer_option`, {
                            option_name: PayerOption.PRESC_FAX,
                            option_description:
                                'Should a prescriber fax be sent upon an auction winner event',
                            option_value: values.sendPrescriberFax === 'Yes' ? 'ON' : 'OFF',
                        }),
                        ...(writeIntakeCaseAdmin
                            ? [
                                  put(`/intake-case/payer_segment_config/${newSegmentId}`, {
                                      send_prior_auth_request:
                                          values.sendPriorAuthRequest === 'Yes',
                                      fax_number: values.intakeCaseFaxNumber,
                                      intake_case_sla_override: values.intakeCaseSlaOverride,
                                      intake_case_archive_override:
                                          values.intakeCaseArchiveOverride,
                                  }),
                              ]
                            : []),
                        ...(writePayerRestrictedAccess
                            ? [
                                  put(
                                      `/aperture/restricted_groups/${values.payerId}/${newSegmentId}`,
                                      {
                                          restricted_group_ids: [
                                              ...new Set(
                                                  [
                                                      ...(values.restrictedGroupIdsToAdd?.split(
                                                          /[\r\n,]/,
                                                      ) ?? []),
                                                      ...values.restrictedGroupIds,
                                                  ]
                                                      .map((groupId) => groupId.trim())
                                                      .filter((id) => id.length > 0),
                                              ),
                                          ],
                                      },
                                  ),
                              ]
                            : []),
                        put(`/reference/payer_segments/${newSegmentId}/billing_instructions`, {
                            pharmacy_billing_instructions: values.pharmacyBillingInstructions,
                            medical_billing_instructions: values.medicalBillingInstructions,
                            bin: values.bin,
                            pcn: values.pcn,
                            rx_group: values.rxGroup,
                        }),
                        ...(testClaimRejectCodeOverrideEnabled
                            ? [
                                  put(
                                      `/treatment/admin/${values.payerId}/available-integrations/test-claim/${newSegmentId}?is_enabled=${values.testClaimRejectCodeOverride === 'Yes'}`,
                                  ),
                              ]
                            : []),
                        put(
                            `/treatment/admin/${values.payerId}/payer_segment_options/${newSegmentId}`,
                            optionInputs,
                        ),
                        put(
                            `/scope-assessor/${values.payerId}/benefit_check_config/${newSegmentId}`,
                            {
                                use_delayed_unmanagement: values.useDelayedUnmanagement === 'Yes',
                                delayed_unmanagement_days:
                                    values.useDelayedUnmanagement === 'Yes'
                                        ? values.delayedUnmanagementDays
                                        : 14,
                            },
                        ),
                    ]);

                    await SuccessToast({
                        message: `Payer Segment ${isCreate ? 'created' : 'updated'} successfully.`,
                    });
                    goHome();
                } catch (err) {
                    handleError('Error saving payer segment.', err);
                }
            }}
        >
            {(formik) => (
                <>
                    <WarningDialog
                        dialogTitle={dialogStrings.unsavedChanges.title}
                        isOpen={showWarning}
                        onSecondaryButtonClick={() => setShowWarning(false)}
                        onPrimaryButtonClick={goHome}
                        dialogContent={<p>{dialogStrings.unsavedChanges.body}</p>}
                    />
                    <FormContainer
                        formTitle={`${isCreate ? 'Create' : 'Edit'} Payer Segment`}
                        onFormSubmit={formik.handleSubmit}
                        onSecondaryButtonClick={() => {
                            if (formik.dirty) {
                                setShowWarning(true);
                            } else {
                                goHome();
                            }
                        }}
                        isLoading={
                            formik.isSubmitting ||
                            !(isCreate || payerSegment || restrictedGroups) ||
                            !payers ||
                            !payerSegments
                        }
                        isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                        formLeftContent={
                            <>
                                <SuggestSingleSelectFormik
                                    label="Payer"
                                    id="payerId"
                                    required
                                    disabled={!isCreate}
                                    values={payersList}
                                    selectedItem={payersList?.find(
                                        ({ secondaryData }) =>
                                            secondaryData === formik.values.payerId,
                                    )}
                                    onChange={(newValue) =>
                                        formik.setFieldValue('payerId', newValue?.secondaryData)
                                    }
                                />
                                <TextInputFormik
                                    label="Payer Segment Name"
                                    id="segmentName"
                                    required
                                />
                                <TextInputFormik label="PBM" id="pbmName" disabled />
                                <RadioButtonGroupFormik
                                    name="sendPrescriberFax"
                                    label="Send Prescriber Fax"
                                    helperText="This is the fax that is sent to the prescriber when the servicing supplier obtains a referral."
                                    required
                                    options={['Yes', 'No']}
                                />
                                <RadioButtonGroupFormik
                                    name="useCBI"
                                    label="Claims Based Intake"
                                    required
                                    options={['Yes', 'No']}
                                />
                                {enableNonPaClaimsBasedIntake && (
                                    <>
                                        <RadioButtonGroupFormik
                                            name="useNonPaCbi"
                                            label="Non-PA Claims Based Intake"
                                            required
                                            options={['Yes', 'No']}
                                        />
                                        {formik.values.useNonPaCbi === 'Yes' && (
                                            <RadioButtonGroupFormik
                                                name="graceFills"
                                                label="Grace Fills"
                                                required
                                                options={['1', '2', '3', '4']}
                                            />
                                        )}
                                    </>
                                )}
                                <RadioButtonGroupFormik
                                    name="testClaimRejectCodeOverride"
                                    label="Use Test Claims upon Intake"
                                    required={testClaimRejectCodeOverrideEnabled}
                                    options={['Yes', 'No']}
                                    disabled={!testClaimRejectCodeOverrideEnabled}
                                />
                                {!testClaimRejectCodeOverrideEnabled && (
                                    <div className="margin__bottom--medium">
                                        <InlineMessage
                                            intent={InlineMessage.Intent.INFO}
                                            wrapText
                                            label="Test claim url is not configured for this payer segment. Contact developer to configure. "
                                        />
                                    </div>
                                )}
                                <RadioButtonGroupFormik
                                    name="sendPriorAuthRequest"
                                    label="Send Prior Authorization Request"
                                    helperText="Send prior authorization requests in Lighthouse"
                                    required
                                    options={['Yes', 'No']}
                                />
                                <PhoneInputFormik
                                    id="intakeCaseFaxNumber"
                                    label="Payer Segment Fax Number"
                                    disabled={!writeIntakeCaseAdmin}
                                />
                                <NumberInputFormik
                                    id="intakeCaseSlaOverride"
                                    label="Active Intake Case Days"
                                    required
                                    disabled={!writeIntakeCaseAdmin}
                                    helperText="The number of days before an auth request will be automatically submitted to the health plan."
                                    step="1"
                                    maxDecimalPlaces={0}
                                />
                                <NumberInputFormik
                                    id="intakeCaseArchiveOverride"
                                    label="Submitted Intake Case Days"
                                    required
                                    disabled={!writeIntakeCaseAdmin}
                                    helperText="The number of days before a submitted intake case will be automatically sent to archived intake cases."
                                    step="1"
                                    maxDecimalPlaces={0}
                                />
                                <NumberInputFormik
                                    id="dispensingFeeAmount"
                                    label="Dispensing Fee Amount"
                                    maxDecimalPlaces={2}
                                />
                                <NumberInputFormik
                                    id="claimsRebillingThreshold"
                                    label="Claims Rebilling Threshold"
                                    helperText="Days to keep claims open and allow rebilling."
                                    maxDecimalPlaces={0}
                                />
                                <TextAreaFormik
                                    label="Restricted Group ID(s)"
                                    id="restrictedGroupIdsToAdd"
                                    helperText="Comma delimited group ID list."
                                    disabled={!writePayerRestrictedAccess}
                                    onChange={(e) =>
                                        formik.setFieldValue(
                                            'restrictedGroupIdsToAdd',
                                            e.target.value?.length > 0 ? e.target.value : undefined,
                                        )
                                    }
                                />
                                <div className="margin__bottom--medium" style={{ maxWidth: 300 }}>
                                    <DetailLabel label="Restricted Groups" />
                                    {formik.values.restrictedGroupIds.map((id) => (
                                        <Tag
                                            key={id}
                                            label={id}
                                            className="margin__right--small margin__bottom--small"
                                            onRemove={
                                                writePayerRestrictedAccess
                                                    ? () =>
                                                          formik.setFieldValue(
                                                              'restrictedGroupIds',
                                                              formik.values.restrictedGroupIds.filter(
                                                                  (groupId) => groupId !== id,
                                                              ),
                                                          )
                                                    : undefined
                                            }
                                        />
                                    ))}
                                </div>
                                <TextAreaFormik
                                    label="Pharmacy Billing Instructions"
                                    id="pharmacyBillingInstructions"
                                    helperText="These instructions will be displayed on the supplier fax and referral details."
                                    fill
                                    onChange={(e) =>
                                        formik.setFieldValue(
                                            'pharmacyBillingInstructions',
                                            e.target.value?.length > 0 ? e.target.value : undefined,
                                        )
                                    }
                                />
                                <TextAreaFormik
                                    label="Medical Billing Instructions"
                                    id="medicalBillingInstructions"
                                    helperText="These instructions will be displayed on the supplier fax and referral details."
                                    fill
                                    onChange={(e) =>
                                        formik.setFieldValue(
                                            'medicalBillingInstructions',
                                            e.target.value?.length > 0 ? e.target.value : undefined,
                                        )
                                    }
                                />
                                <TextInputFormik label="Contract Entity" id="contractEntity" />
                                <TextInputFormik label="BIN" id="bin" />
                                <TextInputFormik label="PCN" id="pcn" />
                                <TextInputFormik label="Rx Group" id="rxGroup" />
                                <ListPageHeader pageTitle="Payer Market Configuration" />
                                {isCreate && (
                                    <RadioButtonGroupFormik
                                        label="Payer Market"
                                        name="payerMarket"
                                        required
                                        options={Object.values(PayerMarketOptions)}
                                    />
                                )}
                                {formik.values.payerMarket === PayerMarketOptions.CREATE_NEW ? (
                                    <TextInputFormik
                                        label="Payer Market Name"
                                        id="payerMarketName"
                                        required
                                        disabled={!isCreate}
                                    />
                                ) : (
                                    <SuggestSingleSelectFormik
                                        label="Payer Market Name"
                                        id="payerMarketId"
                                        required
                                        disabled={!isCreate}
                                        values={payerMarketOptions}
                                        selectedItem={payerMarketOptions?.find(
                                            ({ payerMarketId }) =>
                                                payerMarketId === formik.values.payerMarketId,
                                        )}
                                        onChange={(newValue) =>
                                            formik.setFieldValue(
                                                'payerMarketId',
                                                newValue?.payerMarketId,
                                            )
                                        }
                                    />
                                )}
                                {lucentEnhancedEligibility && (
                                    <>
                                        <ListPageHeader pageTitle="Member Eligibility Monitoring" />
                                        <RadioButtonGroupFormik
                                            name="useDelayedUnmanagement"
                                            label="Monitor Member Eligibility?"
                                            helperText="This is to monitor for eligibility for a specified number of days when member enrollment is inactive."
                                            required
                                            options={['Yes', 'No']}
                                        />
                                        {formik.values.useDelayedUnmanagement === 'Yes' && (
                                            <NumberInputFormik
                                                id="delayedUnmanagementDays"
                                                label="Member Eligibility Monitoring Days"
                                                required
                                                helperText="The number of days member eligibility will be monitoried before unmanaging treatment."
                                            />
                                        )}
                                    </>
                                )}
                                <ListPageHeader pageTitle="Therapy Group Handling" />
                                <RadioButtonGroupFormik
                                    name="useReferralCreationPolicy"
                                    label="Enable Therapy Group Handling"
                                    helperText="Referral creation under this segment will be gated: single referral per treatment cohort (member + therapy group), product selection through preferred product configuration and eligibility."
                                    options={['Yes', 'No']}
                                />
                                <ListPageHeader pageTitle="Copay Message Configuration" />
                                <div className="margin__bottom--medium">
                                    <InlineMessage
                                        intent={InlineMessage.Intent.INFO}
                                        label="The copay message will be shown on referrals and faxes where copay programs are available for the member and drug."
                                    />
                                </div>
                                <TextInputFormik
                                    label="Copay Title"
                                    id="copayMessageTitle"
                                    helperText="The title will appear with the short and long description in referral details and on pharmacy faxes."
                                />
                                <TextInputFormik
                                    label="Copay Summary Message"
                                    id="copayMessageShortDescription"
                                    helperText={
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                width: '100%',
                                            }}
                                        >
                                            <span style={{ textAlign: 'left' }}>
                                                The summary message will appear on the notice board
                                                in referral details.
                                            </span>
                                            <span style={{ textAlign: 'right' }}>
                                                {
                                                    stripHTMLTags(
                                                        formik.values
                                                            .copayMessageShortDescription ?? '',
                                                    ).length
                                                }
                                                /250
                                            </span>
                                        </div>
                                    }
                                />
                                <RichTextAreaFormik
                                    label="Copay Full Message"
                                    id="copayMessageLongDescription"
                                    helperText={
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                width: '100%',
                                            }}
                                        >
                                            <span style={{ textAlign: 'left' }}>
                                                This full message will appear when &quot;See
                                                Details&quot; is pressed below the short description
                                                on the notice board in referral details and on
                                                pharmacy faxes.
                                            </span>
                                            <span style={{ textAlign: 'right' }}>
                                                {
                                                    stripHTMLTags(
                                                        formik.values.copayMessageLongDescription ??
                                                            '',
                                                    ).length
                                                }
                                                /1500
                                            </span>
                                        </div>
                                    }
                                />
                            </>
                        }
                    />
                </>
            )}
        </Formik>
    );
};
